import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { Slider } from '../ui/slider';
import { Building2, MapPin, Calculator, Download, Eye, Save, RefreshCw, Leaf, Info } from 'lucide-react';
import { TERRITOIRES_DOM, TYPES_PROJET, STRUCTURES, COMPLEXITE_SOL, RiskGauge, ContributionAllocation } from './constants';

export default function SimulateurTab({ ctx }) {
  const { calculating, saving, showProgrammeMode, formData, result, savedSimulation, handleSave, handleViewAttestation, handleDownloadPDF, updateForm } = ctx;
  return (
        <TabsContent value="simulateur" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formulaire */}
            <div className="space-y-6">
              {/* Type de projet */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="h-5 w-5 text-emerald-600" />
                    Type de projet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 gap-3">
                    {TYPES_PROJET.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => updateForm('type_projet', type.value)}
                        data-testid={`type-projet-${type.value}`}
                        className={`p-4 rounded-xl border-2 transition-all text-center ${
                          formData.type_projet === type.value
                            ? 'border-emerald-500 bg-emerald-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <type.icon className={`h-8 w-8 mx-auto mb-2 ${
                          formData.type_projet === type.value ? 'text-emerald-600' : 'text-gray-400'
                        }`} />
                        <div className="font-medium text-sm">{type.label}</div>
                      </button>
                    ))}
                  </div>
                  {formData.type_projet === 'programme' && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2 text-blue-800">
                        <Info className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          Mode Programme Multi-lots activé. Utilisez l&apos;onglet &quot;Programmes&quot; pour créer un programme complet.
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Territoire - 7 DOM */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <MapPin className="h-5 w-5 text-emerald-600" />
                    Territoire DOM
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Select value={formData.territoire} onValueChange={(v) => updateForm('territoire', v)}>
                    <SelectTrigger data-testid="select-territoire">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TERRITOIRES_DOM.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          <div className="flex items-center gap-2">
                            <span>{t.emoji}</span>
                            <span>{t.label}</span>
                            <Badge variant="outline" className="ml-2 text-xs">{t.risk}</Badge>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </CardContent>
              </Card>
              
              {/* Données financières (projets simples) */}
              {!showProgrammeMode && (
                <>
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Calculator className="h-5 w-5 text-emerald-600" />
                        Données du projet
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Montant travaux (€)</Label>
                          <Input
                            type="number"
                            value={formData.montant_travaux_ht}
                            onChange={(e) => updateForm('montant_travaux_ht', parseFloat(e.target.value) || 0)}
                            className="mt-1"
                            data-testid="input-montant-ht"
                          />
                        </div>
                        <div>
                          <Label>Surface (m²)</Label>
                          <Input
                            type="number"
                            value={formData.surface}
                            onChange={(e) => updateForm('surface', parseFloat(e.target.value) || 0)}
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Durée chantier (mois)</Label>
                          <Input
                            type="number"
                            value={formData.duree_chantier}
                            onChange={(e) => updateForm('duree_chantier', parseInt(e.target.value) || 12)}
                            className="mt-1"
                            min={1}
                            max={60}
                          />
                        </div>
                        <div>
                          <Label>Structure</Label>
                          <Select value={formData.structure} onValueChange={(v) => updateForm('structure', v)}>
                            <SelectTrigger className="mt-1">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {STRUCTURES.map((s) => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div>
                        <Label>Complexité du sol</Label>
                        <Select value={formData.complexite_sol} onValueChange={(v) => updateForm('complexite_sol', v)}>
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {COMPLEXITE_SOL.map((s) => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label} - {s.description}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Paramètres ESS */}
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Leaf className="h-5 w-5 text-emerald-600" />
                        Paramètres ESS & Prévention
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label>Prévention intégrée</Label>
                          <p className="text-xs text-gray-500">Mesures de prévention documentées</p>
                        </div>
                        <Switch
                          checked={formData.prevention_integree}
                          onCheckedChange={(v) => updateForm('prevention_integree', v)}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <Label>Audit technique préalable</Label>
                          <p className="text-xs text-gray-500">Expertise avant travaux</p>
                        </div>
                        <Switch
                          checked={formData.audit_technique}
                          onCheckedChange={(v) => updateForm('audit_technique', v)}
                        />
                      </div>
                      
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <Label>Part des entreprises ESS (%)</Label>
                          <Badge variant="outline">{formData.part_ess_entreprises}%</Badge>
                        </div>
                        <Slider
                          value={[formData.part_ess_entreprises]}
                          onValueChange={([v]) => updateForm('part_ess_entreprises', v)}
                          max={100}
                          step={10}
                          className="mt-2"
                        />
                      </div>
                    </CardContent>
                  </Card>
                </>
              )}
            </div>
            
            {/* Résultats */}
            <div className="space-y-6">
              {!showProgrammeMode && (
                <Card className="border-2 border-emerald-200 bg-emerald-50/50">
                  <CardHeader>
                    <CardTitle className="text-lg">Résultat de la simulation</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {calculating ? (
                      <div className="flex items-center justify-center py-8">
                        <RefreshCw className="h-8 w-8 text-emerald-600 animate-spin" />
                      </div>
                    ) : result ? (
                      <>
                        <RiskGauge 
                          score={result.calcul.score_risque} 
                          niveau={result.calcul.niveau_risque} 
                        />
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div className="bg-white p-4 rounded-xl border">
                            <div className="text-sm text-gray-500">Taux contribution</div>
                            <div className="text-2xl font-bold text-emerald-700">
                              {result.calcul.taux_contribution}%
                            </div>
                          </div>
                          <div className="bg-white p-4 rounded-xl border">
                            <div className="text-sm text-gray-500">Plafond intervention</div>
                            <div className="text-2xl font-bold text-amber-600">
                              {result.calcul.plafond_intervention?.toLocaleString('fr-FR')} €
                            </div>
                          </div>
                        </div>
                        
                        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-6 rounded-xl text-center">
                          <div className="text-sm opacity-90">Contribution DOS</div>
                          <div className="text-4xl font-bold mt-2" data-testid="contribution-dos">
                            {result.calcul.contribution_dos?.toLocaleString('fr-FR')} €
                          </div>
                          <div className="text-sm opacity-75 mt-1">
                            Sur {result.calcul.montant_travaux_ht?.toLocaleString('fr-FR')} € (Montant de base)
                          </div>
                        </div>
                        
                        <ContributionAllocation affectation={result.calcul.affectation} />
                        
                        {/* Actions */}
                        <div className="flex gap-3">
                          <Button 
                            onClick={handleSave}
                            disabled={saving}
                            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                            data-testid="btn-save-simulation"
                          >
                            <Save className="h-4 w-4 mr-2" />
                            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                          </Button>
                          {savedSimulation && (
                            <>
                              <Button 
                                variant="outline"
                                onClick={() => handleViewAttestation(savedSimulation.id)}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                Voir
                              </Button>
                              <Button 
                                variant="outline"
                                onClick={() => handleDownloadPDF(savedSimulation.id)}
                              >
                                <Download className="h-4 w-4 mr-2" />
                                PDF
                              </Button>
                            </>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Calculator className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>Renseignez les données pour obtenir une simulation</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Info territoire */}
              {result?.calcul?.territoire_config && !showProgrammeMode && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <MapPin className="h-4 w-4" />
                      Spécificités territoire
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-gray-500">Zone sismique</div>
                        <div className="font-semibold">Niveau {result.calcul.territoire_config.zone_sismique}</div>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <div className="text-gray-500">Zone cyclonique</div>
                        <div className="font-semibold">{result.calcul.territoire_config.zone_cyclonique ? 'Oui' : 'Non'}</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </TabsContent>
  );
}
